import React, { useEffect, useState, useMemo } from 'react';
import { settingsAPI } from '../../services/api';
import { Lock, Save, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Account Section Access Control (iter221)
 *
 * Admin-managed matrix that governs which non-Admin roles can enter data
 * into which Account sections. First-save behaviour and lock enforcement
 * live on the backend (`services/section_access.py`) — this page only
 * writes the config.
 */
const roleLabels = {
  cx: 'CX',
  cx_leader: 'CX Leader',
  gtm_sales: 'GTM Sales (Lead Owner)',
  gtm_sales_leader: 'GTM Sales Leader',
  qc: 'QC',
  bfr: 'BFR',
};

export default function SectionAccessSettings() {
  const [meta, setMeta] = useState({ sections: [], roles: [] });
  const [matrix, setMatrix] = useState({}); // matrix[role][section] = bool
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const buildMatrix = (rules, roles, sections) => {
    const m = {};
    roles.forEach(r => { m[r] = {}; sections.forEach(s => { m[r][s.key] = false; }); });
    (rules || []).forEach(({ scope, key, section, enabled }) => {
      if (scope === 'role' && m[key] && section in m[key]) m[key][section] = !!enabled;
    });
    return m;
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.getSectionAccess();
        const data = res.data || {};
        setMeta({ sections: data.sections || [], roles: data.roles || [] });
        setMatrix(buildMatrix(data.rules || [], data.roles || [], data.sections || []));
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Failed to load section access config.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (role, section) => {
    setMatrix(prev => ({
      ...prev,
      [role]: { ...(prev[role] || {}), [section]: !((prev[role] || {})[section]) },
    }));
  };

  const toggleAllForRole = (role, val) => {
    setMatrix(prev => {
      const next = { ...(prev[role] || {}) };
      meta.sections.forEach(s => { next[s.key] = val; });
      return { ...prev, [role]: next };
    });
  };

  const rulesPayload = useMemo(() => {
    const rules = [];
    Object.entries(matrix || {}).forEach(([role, secMap]) => {
      Object.entries(secMap || {}).forEach(([section, enabled]) => {
        rules.push({ scope: 'role', key: role, section, enabled: !!enabled });
      });
    });
    return rules;
  }, [matrix]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsAPI.updateSectionAccess(rulesPayload);
      toast.success('Section access rules saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="p-8 flex items-center gap-2 text-gray-600" data-testid="section-access-loading">
      <Loader2 className="animate-spin" size={18} /> Loading…
    </div>
  );

  return (
    <div className="p-6" data-testid="section-access-page">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Lock size={22} className="text-indigo-600" /> Account Section Access
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Choose which non-Admin roles may enter data into each Account section.
            Once a user submits a section for the first time, that section becomes
            read-only for them — only Admin can edit it afterwards.
            <span className="block mt-1 text-xs text-amber-700">
              <ShieldAlert size={12} className="inline -mt-0.5 mr-1" />
              Admin always retains full edit access, regardless of this configuration.
            </span>
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:opacity-60"
          data-testid="section-access-save-btn"
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save
        </button>
      </div>

      <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-700">Role</th>
              {meta.sections.map(s => (
                <th key={s.key} className="text-center px-3 py-3 font-semibold text-gray-700 whitespace-nowrap">
                  {s.label}
                </th>
              ))}
              <th className="text-center px-3 py-3 font-semibold text-gray-500">All</th>
            </tr>
          </thead>
          <tbody>
            {meta.roles.map(role => (
              <tr key={role} className="border-b border-gray-100 hover:bg-gray-50/60">
                <td className="px-4 py-3 font-medium text-gray-800">
                  {roleLabels[role] || role}
                </td>
                {meta.sections.map(s => {
                  const on = !!(matrix[role] && matrix[role][s.key]);
                  return (
                    <td key={s.key} className="px-3 py-3 text-center">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggle(role, s.key)}
                          className="w-4 h-4 accent-indigo-600 cursor-pointer"
                          data-testid={`sac-toggle-${role}-${s.key}`}
                        />
                      </label>
                    </td>
                  );
                })}
                <td className="px-3 py-3 text-center whitespace-nowrap">
                  <button
                    onClick={() => toggleAllForRole(role, true)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 mr-2"
                    data-testid={`sac-all-on-${role}`}
                  >All</button>
                  <button
                    onClick={() => toggleAllForRole(role, false)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                    data-testid={`sac-all-off-${role}`}
                  >None</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-gray-500">
        A tick means "this role may enter data in this section". A first save
        immediately locks the section for that user — subsequent edits must be
        performed by an Admin. GTM Sales access is additionally gated by
        Lead-Owner: a GTM Sales user who isn't the Lead Owner cannot edit,
        regardless of this setting.
      </p>
    </div>
  );
}
